import type { UpdateAttendeesInput } from '@/components/Event/NewScript/hooks/state/stateActions'
import {
  ATTENDEES_HAVE_LEFT_CALL_ACTION_KEY,
  CLOSING_ACTIONS_UNSKIPPED_ON_CANCELLATION,
  NORMAL_CLOSING_ACTIONS,
} from '@/components/Event/NewScript/hooks/state/stateActions'
import { useScriptState } from '@/components/Event/NewScript/hooks/state/useScriptState'
import { useStatePolicies } from '@/components/Event/NewScript/hooks/useStatePolicy'
import { isExaminationUnderOath } from '@/components/Event/NewScript/utils/event'
import { parseStepKey } from '@/components/Event/NewScript/utils/stepKey'
import { ScriptStep, ScriptStepStatus } from '@/constants'
import { Event } from '@/types'

type StepAction = {
  completed?: boolean
  skip?: boolean
  optional?: boolean
}

type UseStepActionsOptions = {
  hiddenSatisfiedActions?: string[]
}

export const isActionSatisfiedForStep = ({
  actionKey,
  action,
  step,
  eventCount,
  hiddenSatisfiedActions = [],
}: {
  actionKey: string
  action: StepAction
  step: ScriptStep | string
  eventCount: number
  hiddenSatisfiedActions?: string[]
}): boolean => {
  const { step: stepKey, witnessIndex } = parseStepKey(step)
  const parsedWitnessIndex =
    witnessIndex !== undefined ? Number(witnessIndex) : undefined
  const isNonFinalIndexedClosing =
    stepKey === ScriptStep.CLOSING &&
    parsedWitnessIndex !== undefined &&
    !Number.isNaN(parsedWitnessIndex) &&
    parsedWitnessIndex < eventCount - 1

  if (
    actionKey === ATTENDEES_HAVE_LEFT_CALL_ACTION_KEY &&
    isNonFinalIndexedClosing
  ) {
    return true
  }

  if (hiddenSatisfiedActions.includes(actionKey)) {
    return true
  }

  return Boolean(action.optional || action.skip || action.completed)
}

export const useStepActions = (
  step: ScriptStep | string,
  event: Event,
  { hiddenSatisfiedActions = [] }: UseStepActionsOptions = {},
) => {
  const { actions, state } = useScriptState()
  const { canHaveCna, shouldConfirmStipulations } = useStatePolicies(event)

  const isActionCompleted = (key: string): boolean => {
    return state.steps[step].actions[key]?.completed ?? false
  }

  const canContinue = () => {
    if (
      [ScriptStepStatus.COMPLETED, ScriptStepStatus.SKIPPED].includes(
        state.steps[step].status,
      )
    ) {
      return false
    }

    return Object.entries(state.steps[step].actions).every(
      ([actionKey, action]) =>
        isActionSatisfiedForStep({
          actionKey,
          action,
          step,
          eventCount: state.events.length,
          hiddenSatisfiedActions,
        }),
    )
  }

  const completeAction = (key: string) => {
    return actions.completeStepAction({ step: step, key })
  }

  const updateAttendeesAndCompleteAction = (
    input: UpdateAttendeesInput,
    key: string,
  ) => {
    return actions.updateAttendeesAndCompleteAction({
      ...input,
      step: step,
      key,
    })
  }

  const undoAction = (key: string) => {
    return actions.undoStepAction({ step: step, key })
  }

  const completeStepAndContinue = (nextStep: ScriptStep | string) => {
    return actions.completeStepAndContinue({ step, nextStep })
  }

  const cancelProceeding = () => {
    const notSkippable = [ScriptStep.CLOSING, ScriptStep.REVIEW]

    let first_skipped_step: string | null = null
    const skipSteps: (ScriptStep | string)[] = []
    const skipStepActions = []
    const unskipStepActions = []

    const currentEventIndex = parseStepKey(state.current_step).witnessIndex
    Object.entries(state.steps)
      .filter(([key]) =>
        currentEventIndex === undefined
          ? true
          : parseStepKey(key).witnessIndex === currentEventIndex,
      )
      .forEach(([key, step]) => {
        if (
          !notSkippable.includes(step.key as ScriptStep) &&
          step.status !== ScriptStepStatus.COMPLETED
        ) {
          if (first_skipped_step === null) {
            first_skipped_step = key
          }
          skipSteps.push(key)
        }
      })

    // Changing optional and non-optional steps
    skipStepActions.push(
      ...NORMAL_CLOSING_ACTIONS.map(key => ({
        step: ScriptStep.CLOSING,
        key,
      })),
    )
    unskipStepActions.push(
      ...CLOSING_ACTIONS_UNSKIPPED_ON_CANCELLATION.map(key => ({
        step: ScriptStep.CLOSING,
        key,
      })),
    )

    actions.cancelProceeding({
      skipSteps,
      skipStepActions,
      unskipStepActions,
      first_skipped_step,
    })
  }

  const revertCanceledProceeding = () => {
    actions.revertCanceledProceeding({
      canHaveCna,
      isExaminationUnderOath: isExaminationUnderOath(event),
      shouldConfirmStipulations,
    })
  }

  return {
    isActionCompleted,
    canContinue,
    completeAction,
    updateAttendeesAndCompleteAction,
    completeStepAndContinue,
    undoAction,
    cancelProceeding,
    revertCanceledProceeding,
  }
}
